import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { ENV } from '../../config/env';

interface BusinessName {
  name: string;
  explanation: string;
  tagline: string;
}

const BusinessNameGenerator: React.FC = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [industry, setIndustry] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [description, setDescription] = useState('');
  const [businessNames, setBusinessNames] = useState<BusinessName[]>([]);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter(k => k !== keyword));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!industry || keywords.length === 0 || !description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const userId = await getToken();
      const response = await fetch(
        `${ENV.API_URL}/api/marketing/business-name`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            userId,
            industry, 
            keywords, 
            description 
          }),
        }
      );
      
      const data = await response.json();
      setBusinessNames(data.businessNames);
      toast.success('Business names generated successfully!');
    } catch (error) {
      console.error('Error generating business names:', error);
      toast.error('Failed to generate business names. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  // Example data for quick-fill
  const exampleData = {
    industry: 'Technology',
    keywords: ['innovative', 'cloud', 'secure'],
    description: 'A SaaS platform helping small businesses manage their operations securely in the cloud.'
  };

  const handleUseExample = () => {
    setIndustry(exampleData.industry);
    setKeywords(exampleData.keywords);
    setKeywordInput('');
    setDescription(exampleData.description);
  };

  return (
    <div className="container mx-auto px-4 py-10 relative overflow-hidden">
      {/* Decorative blurred orbs */}
      <div className="absolute top-40 -left-24 w-72 h-72 bg-gradient-to-br from-blue-200 to-purple-300 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute -top-20 right-10 w-96 h-96 bg-gradient-to-br from-purple-200 to-pink-300 rounded-full opacity-20 blur-3xl"></div>
      
      <div className="relative z-10 mb-10">
        <h1 className="text-4xl font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Business Name Generator</h1>
        <p className="text-gray-700 text-lg max-w-3xl">
          Generate creative and memorable business names based on your industry, values, and target audience.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-xl p-8">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
          
          <div className="relative z-10">
            <h2 className="text-2xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Generate Business Names</h2>
            
            <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="industry" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                Industry <span className="text-pink-500">*</span>
              </label>
              <input
                type="text"
                id="industry"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g. Technology, Food, Healthcare"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="keywords" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                Keywords <span className="text-pink-500">*</span>
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="keywords"
                  className="flex-grow px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-l-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="e.g. innovative, cloud, secure (comma-separated allowed)"
                  onBlur={() => {
                    // On blur, parse comma-separated keywords
                    if (keywordInput.includes(',')) {
                      const newKeywords = keywordInput.split(',').map(k => k.trim()).filter(k => k && !keywords.includes(k));
                      if (newKeywords.length > 0) {
                        setKeywords([...keywords, ...newKeywords]);
                        setKeywordInput('');
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    // Support comma-separated keywords on add
                    if (keywordInput.includes(',')) {
                      const newKeywords = keywordInput.split(',').map(k => k.trim()).filter(k => k && !keywords.includes(k));
                      if (newKeywords.length > 0) {
                        setKeywords([...keywords, ...newKeywords]);
                        setKeywordInput('');
                        return;
                      }
                    }
                    handleAddKeyword();
                  }}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-3 rounded-r-xl hover:from-blue-600 hover:to-purple-700 shadow-sm hover:shadow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 font-medium"
                >
                  Add
                </button>
                <button
                  type="button"
                  className="ml-3 text-xs px-3 py-2 bg-white/40 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/60 transition-all duration-200 font-medium text-gray-700 shadow-sm hover:shadow"
                  onClick={handleUseExample}
                >
                  Use Example
                </button>
              </div>
              
              {keywords.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-100/80 to-purple-100/80 backdrop-blur-sm text-blue-800 border border-blue-200/50 shadow-sm"
                    >
                      {keyword}
                      <button
                        type="button"
                        className="ml-2 inline-flex text-blue-500 hover:text-red-500 focus:outline-none transition-colors duration-200"
                        onClick={() => handleRemoveKeyword(keyword)}
                      >
                        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mb-6">
              <label htmlFor="description" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                Business Description <span className="text-pink-500">*</span>
              </label>
              <textarea
                id="description"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your business idea, target audience, and unique value proposition"
                rows={4}
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-6 bg-gradient-to-r from-blue-500 to-purple-600 border border-transparent rounded-xl shadow-md text-sm font-semibold text-white hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Creating Business Names...</span>
                </div>
              ) : (
                <span className="flex items-center justify-center">
                  Generate Business Names
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              )}
            </button>
          </form>
          </div>
        </div>
        
        <div>
          {businessNames.length > 0 ? (
            <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-xl p-8">
              {/* Subtle gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
              
              <div className="relative z-10">
                <h2 className="text-2xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Generated Business Names</h2>
                
                <div className="space-y-6">
                  {businessNames.map((item, index) => (
                    <div key={index} className="bg-white/40 backdrop-blur-sm border border-white/50 rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200 transform hover:-translate-y-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-bold text-blue-700">{item.name}</h3>
                        <button
                          type="button"
                          className="text-xs px-3 py-1.5 bg-gradient-to-r from-blue-100/80 to-purple-100/80 text-blue-700 rounded-full hover:from-blue-200/80 hover:to-purple-200/80 transition-all duration-200 border border-blue-200/50 shadow-sm font-medium flex items-center"
                          title="Copy business name"
                          onClick={() => {
                            navigator.clipboard.writeText(item.name);
                            toast.success('Business name copied!');
                          }}
                        >
                          <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Copy
                        </button>
                      </div>
                      <p className="text-sm italic text-gray-600 mb-2 border-l-2 border-purple-300 pl-3">"{item.tagline}"</p>
                      <p className="text-gray-700">{item.explanation}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 border-dashed rounded-2xl shadow-lg p-8 text-center h-full flex flex-col items-center justify-center">
              {/* Subtle gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-purple-50/20 opacity-60"></div>
              
              <div className="relative z-10">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/40 backdrop-blur-sm flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">No Business Names Yet</h3>
                <p className="text-gray-700">
                  Fill out the form and click <span className="font-medium">"Generate Business Names"</span> to see results here.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-12 relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-xl p-8">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
        
        <div className="relative z-10">
          <h2 className="text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">How It Works</h2>
          <p className="mb-6 text-gray-700">
            Our Business Name Generator uses advanced AI to create unique, memorable names for your business based on your industry, keywords, and description.
          </p>
          
          <h3 className="text-lg font-semibold mb-3 text-blue-700">Tips for Better Results:</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Be specific about your industry',
              'Add keywords that reflect your brand values',
              'Provide a detailed description of your business concept',
              'Include information about your target audience',
              'Mention any specific naming preferences (short, modern, etc.)'
            ].map((tip, index) => (
              <li key={index} className="flex items-start bg-white/40 backdrop-blur-sm rounded-lg p-3 shadow-sm">
                <svg className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BusinessNameGenerator;
